import React from 'react';

interface ElevationSparklineProps {
  coordinates?: [number, number, number][];
  activityType: string;
}

export const ElevationSparkline: React.FC<ElevationSparklineProps> = ({ coordinates, activityType }) => {
  if (!coordinates || coordinates.length < 2) return null;

  const elevations = coordinates.map((c) => c[2] ?? 0);
  const minEle = Math.min(...elevations);
  const maxEle = Math.max(...elevations);
  const eleRange = maxEle - minEle;

  const height = 24;
  const isFlat = eleRange < 0.5;
  const pad = isFlat ? 10 : eleRange * 0.05;
  const graphMin = isFlat ? minEle - 10 : minEle - pad;
  const graphMax = isFlat ? maxEle + 10 : maxEle + pad;
  const graphRange = graphMax - graphMin || 1;

  const pointsPath = elevations
    .map((e, idx) => {
      const x = (idx / (elevations.length - 1)) * 100;
      const y = height - ((e - graphMin) / graphRange) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' L ');

  const strokePath = `M ${pointsPath}`;
  const fillPath = `${strokePath} L 100,${height} L 0,${height} Z`;

  let strokeColor = '#3b82f6';
  let fillColor = 'rgba(59, 130, 246, 0.08)';
  if (activityType === 'running') {
    strokeColor = '#ff385c';
    fillColor = 'rgba(255, 56, 92, 0.08)';
  } else if (activityType === 'cycling') {
    strokeColor = '#10b981';
    fillColor = 'rgba(16, 185, 129, 0.08)';
  } else if (activityType === 'hiking') {
    strokeColor = '#f59e0b';
    fillColor = 'rgba(245, 158, 11, 0.08)';
  } else if (activityType === 'climbing') {
    strokeColor = '#a855f7';
    fillColor = 'rgba(168, 85, 247, 0.08)';
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: `${height}px`, marginTop: '8px', overflow: 'hidden' }}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <path d={fillPath} fill={fillColor} />
        <path
          d={strokePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 8px',
          pointerEvents: 'none',
          fontSize: '9px',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        <span>{Math.round(minEle)}m</span>
        <span>{Math.round(maxEle)}m</span>
      </div>
    </div>
  );
};
